import { gql } from "@apollo/client";
import { PriceableItemOrigin } from "elephant-web/gql";
import { useMemo, useState } from "react";
import { useSearchInvestigationItemsLazyQuery } from "./__generated__/use-search-investigation-lazy-query.gql";
import { sortFilterAndPaginate } from "./helpers";
import type {
  SearchNewItemData,
  SearchNewItemDataRow,
  SearchNewItemHook,
  SearchNewItemHookResult,
  SearchNewItemParams,
} from "./types";

gql`
  query searchInvestigationItems {
    catalogueInvestigationActivities {
      _id
      title
    }
  }
`;

const mapGqlResponseToResult = (
  response: ReturnType<typeof useSearchInvestigationItemsLazyQuery>[1],
  params: SearchNewItemParams,
): SearchNewItemData => {
  // catalogueInvestigationActivities returned fields are optionals
  // so we filter just in case the required fields are null or undefined
  const investigations: SearchNewItemDataRow[] =
    (response.data?.catalogueInvestigationActivities
      .map((investigation) =>
        investigation?._id && investigation.title
          ? {
              id: investigation._id,
              name: investigation.title,
              origin: PriceableItemOrigin.Investigation,
            }
          : null,
      )
      .filter(Boolean) as SearchNewItemDataRow[]) ?? [];

  const rows = sortFilterAndPaginate(investigations, params);
  const data = {
    rows: rows ?? [],
    totalRows: rows?.length,
  };

  return {
    ...response,
    data,
  };
};

const useSearchInvestigationItemLazyQuery: SearchNewItemHook = () => {
  const [data, setData] = useState<SearchNewItemData>({
    data: {
      rows: [],
      totalRows: 0,
    },
    loading: false,
  });
  const [query] = useSearchInvestigationItemsLazyQuery();

  const wrappedQuery: SearchNewItemHookResult[0] = useMemo(
    () => (args) =>
      query().then((res) => {
        const newData = mapGqlResponseToResult(res, args);
        setData(newData);
        return newData;
      }),
    [query],
  );

  return [wrappedQuery, data];
};

export default useSearchInvestigationItemLazyQuery;
